// src/shared/stores/useRequestsStore.ts
import { create } from 'zustand';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/config';
import {getAndSanitize} from '../../shared/utils/sanitization';

// Definición de tipos para las solicitudes
interface Request {
  id: string;
  type: "LOAD" | "WITHDRAW";
  status: string;
  adminId?: string;
  user?: {
    username?: string;
    accountName?: string;
    userId?: string;
    historyData?: any[];
    currentBalance?: number;
  };
  createdAt: string;
  processedAt?: string;
  uniqueKey?: string;
  operation?: {
    amount: number;
    bonus: number;
  };
  withdrawAmount?: number;
  linkedTransfer?: {
    amount: number;
  };
  requestNumber?: string;
  withdrawCvuCbu?: string;
  withdrawAlias?: string;
  withdrawHolder?: string;
  requestData?: {
    processingSteps?: Array<{
      timestamp: string | number;
      step: string;
      status: string;
      metadata?: any;
    }>;
    [key: string]: any;
  };
  processingSteps?: any[];
  retryHistory?: any[];
  transactionId?: string;
  [key: string]: any;
}

interface TablePagination {
  current: number;
  pageSize: number;
  total: number;
}

interface BatchRequestsResult {
  pending: { data: Request[]; total: number };
  approved: { data: Request[]; total: number };
  completed: { data: Request[]; total: number };
  metadata: {
    page: number;
    limit: number;
    totalRequests: number;
  };
}

interface RequestsState {
  // Datos de las solicitudes
  pendingRequests: Request[];
  approvedWithdraws: Request[];
  completedRejected: Request[];

  // Estado de carga para cada tipo
  isLoading: {
    pending: boolean;
    approvedWithdraws: boolean;
    completedRejected: boolean;
  };

  // Manejo de errores
  error: {
    pending: Error | null;
    approvedWithdraws: Error | null;
    completedRejected: Error | null;
  };

  // Tiempo de última carga
  lastFetched: {
    pending: number | null;
    approvedWithdraws: number | null;
    completedRejected: number | null;
  };

  // Configuración de paginación
  pagination: {
    pending: TablePagination;
    approvedWithdraws: TablePagination;
    completedRejected: TablePagination;
  };

  // Métodos
  fetchRequests: (
    type: "pending" | "approvedWithdraws" | "completedRejected",
    page: number,
    pageSize: number,
    filters?: any,
    forceRefresh?: boolean,
    effectiveAdminId?: string | null // Add parameter for effective admin ID
  ) => Promise<Request[]>;

  // NEW - Optimized batch fetch (reduces 3+ API calls to 1)
  fetchRequestsBatch: (
    page: number,
    pageSize: number,
    filters?: any,
    forceRefresh?: boolean,
    effectiveAdminId?: string | null
  ) => Promise<BatchRequestsResult>;

  // Método para manejar actualizaciones de solicitudes (desde socket)
  handleRequestUpdate: (request: Request) => Promise<void>;

  // Limpiar datos
  clearRequestsData: () => void;

  // Actualizar paginación
  updatePagination: (
    type: "pending" | "approvedWithdraws" | "completedRejected",
    pagination: Partial<TablePagination>
  ) => void;
}

// Tiempo de cache en milisegundos (1 minuto)
const CACHE_TIME = 1 * 60 * 1000;

// Seguimiento de solicitudes en vuelo para evitar duplicados
const currentRequests: Record<string, Promise<Request[]>> = {};

// Almacenar el último valor de filtro reprocessed usado por tipo
const lastReprocessedValue: Record<string, boolean | undefined> = {
  pending: undefined,
  approvedWithdraws: undefined,
  completedRejected: undefined,
};

// Debounce timer para refetch tras actualizaciones de socket
let refreshDebounceTimer: NodeJS.Timeout | null = null;
const REFRESH_DEBOUNCE_MS = 500; // Esperar 500ms después de la última actualización

export const useRequestsStore = create<RequestsState>((set, get) => ({
  // Estado inicial
  pendingRequests: [],
  approvedWithdraws: [],
  completedRejected: [],
  
  isLoading: {
    pending: false,
    approvedWithdraws: false,
    completedRejected: false,
  },
  
  error: {
    pending: null,
    approvedWithdraws: null,
    completedRejected: null,
  },
  
  lastFetched: {
    pending: null,
    approvedWithdraws: null,
    completedRejected: null,
  },
  
  pagination: {
    pending: { current: 1, pageSize: 10, total: 0 },
    approvedWithdraws: { current: 1, pageSize: 10, total: 0 },
    completedRejected: { current: 1, pageSize: 10, total: 0 },
  },

  // Función principal para obtener solicitudes
  fetchRequests: async (
    type: "pending" | "approvedWithdraws" | "completedRejected",
    page: number,
    pageSize: number,
    filters = {},
    forceRefresh = false,
    effectiveAdminId?: string | null // Add parameter for effective admin ID
  ) => {
    const { lastFetched, pagination } = get();
    const currentTime = Date.now();
    
    // Verificar si el filtro de reprocessed ha cambiado
    const reprocessedChanged = 
      filters.reprocessed !== undefined && 
      lastReprocessedValue[type] !== filters.reprocessed;
    
    // Actualizar el valor de reprocessed para futuras comparaciones
    lastReprocessedValue[type] = filters.reprocessed;
    
    // Si el filtro reprocessed ha cambiado, forzar actualización
    if (reprocessedChanged) {
      forceRefresh = true;
    }
    
    // Generar clave única para esta solicitud (combinación de tipo, página, tamaño y filtros)
    const requestKey = `${type}-${page}-${pageSize}-${JSON.stringify(filters)}`;
    
    // Usar datos en caché si están frescos y los parámetros coinciden
    if (
      !forceRefresh &&
      lastFetched[type] && 
      currentTime - lastFetched[type]! < CACHE_TIME &&
      pagination[type].current === page &&
      pagination[type].pageSize === pageSize
    ) {
      const dataKey = type === 'pending' 
        ? 'pendingRequests' 
        : type === 'approvedWithdraws' 
          ? 'approvedWithdraws' 
          : 'completedRejected';
      return get()[dataKey];
    }
    
    // Reutilizar solicitud en vuelo si existe
    if (currentRequests[requestKey]) {
      return currentRequests[requestKey];
    }
    
    // Actualizar estado de carga
    set(state => ({
      isLoading: {
        ...state.isLoading,
        [type]: true
      },
      error: {
        ...state.error,
        [type]: null
      }
    }));
    
    try {
      // Almacenar promesa para deduplicar solicitudes concurrentes
      currentRequests[requestKey] = new Promise(async (resolve, reject) => {
        try {
          const token = getAndSanitize("token");
          if (!token) throw new Error("No token found");

          // Use effectiveAdminId if provided (from AdminSelectorContext)
          // If null, it means SuperAdmin is viewing all admins' data
          let adminId = effectiveAdminId;

          // If not provided, fall back to token userId (for backwards compatibility)
          if (adminId === undefined) {
            try {
              const tokenData = JSON.parse(atob(token.split(".")[1]));
              adminId = tokenData.userId;
            } catch (err) {
              console.error("[RequestsStore] Failed to extract adminId from token:", err);
              throw new Error("No se pudo obtener el adminId del token");
            }
          } else if (adminId === null) {
          } else {
          }

          // Configurar parámetros según el tipo de solicitud
          let url = `${API_BASE_URL}/api/requests/load-requests`;
          let params: Record<string, any> = {
            page,
            limit: pageSize,
            sortBy: "createdAt",
            sortOrder: "desc",
          };

          // Only include adminId if it's not null (null = see all for SuperAdmin)
          if (adminId !== null) {
            params.adminId = adminId;
          }
          
          // Configurar filtros específicos según el tipo de solicitud
          if (type === "pending") {
            params.status = "PENDING,REQUIRES_VALIDATION";
            // No filtrar por tipo para incluir tanto LOAD como WITHDRAW pendientes
          } else if (type === "approvedWithdraws") {
            params.status = "APPROVED,WAITING_FOR_CLIENT_VALIDATION";
            params.type = "WITHDRAW"; // Filtrar específicamente por retiros
          } else if (type === "completedRejected") {
            params.status = "COMPLETED,REJECTED,CANCELED";
            // No filtrar por tipo para mostrar tanto LOAD como WITHDRAW
          }
          
          // Aplicar filtros adicionales si se proporcionan
          if (filters) {
            // Filtro de nombre de usuario
            if (filters.username) {
              params.username = filters.username;
            }
            
            // Filtro de número de solicitud
            if (filters.requestNumber) {
              params.requestNumber = filters.requestNumber;
            }
            
            // Sobrescribir el estado predeterminado si se proporciona
            if (filters.status) {
              params.status = filters.status;
            }
            
            // Sobrescribir el tipo predeterminado si se proporciona
            if (filters.type) {
              params.type = filters.type;
            }
            
            // Filtros de monto
            if (filters.amountMin) {
              params.amountMin = filters.amountMin;
            }
            
            if (filters.amountMax) {
              params.amountMax = filters.amountMax;
            }
            
            // Filtro de reprocesado
            if (filters.reprocessed !== undefined) {
              params.reprocessed = String(filters.reprocessed);
            }
            
            // Filtros de fecha
            if (filters.dateRange && filters.dateRange.length === 2) {
              params.dateStart = filters.dateRange[0].format("YYYY-MM-DD");
              params.dateEnd = filters.dateRange[1].format("YYYY-MM-DD");
            }
          }
          
          
          const response = await axios.get(url, {
            params,
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              // Only include x-admin-id if not null
              ...(adminId !== null && { "x-admin-id": adminId }),
            },
          });
          

          // 🔍 LOG: Verificar datos de gamificación en respuesta del backend
          if (response.data.data && response.data.data.length > 0) {
            response.data.data.forEach((req: any, _: number) => {
              if (req.operation) {
              }
              if (req.requestData) {
              }
            });
          }

          if (response.data.success) {
            const data = response.data.data || [];
            const total = response.data.total || 0;
            
            // Actualizar el estado según el tipo de solicitud
            set(state => {
              const newState = { ...state };
              
              // Actualizar datos
              if (type === "pending") {
                newState.pendingRequests = data;
              } else if (type === "approvedWithdraws") {
                newState.approvedWithdraws = data;
              } else if (type === "completedRejected") {
                newState.completedRejected = data;
              }
              
              // Actualizar paginación
              newState.pagination = {
                ...state.pagination,
                [type]: {
                  current: page,
                  pageSize: pageSize,
                  total: total,
                }
              };
              
              // Actualizar estado de carga y última actualización
              newState.isLoading = {
                ...state.isLoading,
                [type]: false
              };
              
              newState.lastFetched = {
                ...state.lastFetched,
                [type]: Date.now()
              };
              
              return newState;
            });
            
            resolve(data);
          } else {
            throw new Error(`Failed to fetch ${type} data: ${response.data.message || 'Unknown error'}`);
          }
        } catch (error) {
          const typedError = error as Error;
          set({
            isLoading: {
              ...get().isLoading,
              [type]: false
            },
            error: {
              ...get().error,
              [type]: typedError
            }
          });
          reject(typedError);
        } finally {
          // Limpiar solicitud en seguimiento
          delete currentRequests[requestKey];
        }
      });
      
      return await currentRequests[requestKey];
    } catch (error) {
      console.error(`[RequestsStore] Error fetching ${type} requests:`, error);
      return [];
    }
  },
  
  // NEW - Optimized batch fetch method (reduces 3+ API calls to 1)
  fetchRequestsBatch: async (
    page: number,
    pageSize: number,
    filters = {},
    forceRefresh = false,
    effectiveAdminId?: string | null
  ) => {
    const { lastFetched, pagination } = get();
    const currentTime = Date.now();

    // Check if we should use cache (all categories fresh)
    const allCategoriesFresh =
      !forceRefresh &&
      lastFetched.pending && currentTime - lastFetched.pending < CACHE_TIME &&
      lastFetched.approvedWithdraws && currentTime - lastFetched.approvedWithdraws < CACHE_TIME &&
      lastFetched.completedRejected && currentTime - lastFetched.completedRejected < CACHE_TIME &&
      pagination.pending.current === page &&
      pagination.pending.pageSize === pageSize;

    if (allCategoriesFresh) {
      return {
        pending: { data: get().pendingRequests, total: pagination.pending.total },
        approved: { data: get().approvedWithdraws, total: pagination.approvedWithdraws.total },
        completed: { data: get().completedRejected, total: pagination.completedRejected.total },
        metadata: {
          page,
          limit: pageSize,
          totalRequests: pagination.pending.total + pagination.approvedWithdraws.total + pagination.completedRejected.total
        }
      };
    }

    // Set all categories to loading
    set({
      isLoading: {
        pending: true,
        approvedWithdraws: true,
        completedRejected: true
      },
      error: {
        pending: null,
        approvedWithdraws: null,
        completedRejected: null
      }
    });

    try {
      const token = getAndSanitize("token");
      if (!token) throw new Error("No token found");

      // Use effectiveAdminId if provided
      let adminId = effectiveAdminId;

      if (adminId === undefined) {
        try {
          const tokenData = JSON.parse(atob(token.split(".")[1]));
          adminId = tokenData.userId;
        } catch (err) {
          console.error("[RequestsStore] Failed to extract adminId from token:", err);
          throw new Error("No se pudo obtener el adminId del token");
        }
      } else if (adminId === null) {
      } else {
      }

      // Build params for batch request
      const params: Record<string, any> = {
        page,
        limit: pageSize,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      // Only include adminId if it's not null
      if (adminId !== null) {
        params.adminId = adminId;
      }

      // Apply additional filters
      if (filters.username) params.username = filters.username;
      if (filters.requestNumber) params.requestNumber = filters.requestNumber;
      if (filters.amountMin) params.amountMin = filters.amountMin;
      if (filters.amountMax) params.amountMax = filters.amountMax;
      if (filters.reprocessed !== undefined) params.reprocessed = String(filters.reprocessed);
      if (filters.status) params.status = filters.status;
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.dateStart = filters.dateRange[0].format("YYYY-MM-DD");
        params.dateEnd = filters.dateRange[1].format("YYYY-MM-DD");
      }

      // Single API call to batch endpoint
      const response = await axios.get(`${API_BASE_URL}/api/requests/load-requests-batch`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...(adminId !== null && { "x-admin-id": adminId }),
        },
      });



      if (response.data.success) {
        const batchData = response.data.data;

        // Parse requestData for each record (normalize timestamps and defaults)
        const parseRequestData = (req: any): any => {
          if (!req.requestData) return req;

          const rd = typeof req.requestData === 'string'
            ? (() => { try { return JSON.parse(req.requestData); } catch { return null; } })()
            : req.requestData;

          if (!rd) return { ...req, requestData: null };

          return {
            ...req,
            requestData: {
              ...rd,
              processingSteps: (rd.processingSteps || []).map((step: any) => ({
                ...step,
                timestamp: step.timestamp ? new Date(step.timestamp) : new Date(),
                metadata: {
                  ...step.metadata,
                  comparedTransfers: step.metadata?.comparedTransfers ?? [],
                },
              })),
            },
          };
        };

        const pendingData = (batchData.pending.data || []).map(parseRequestData);
        const approvedData = (batchData.approved.data || []).map(parseRequestData);
        const completedData = (batchData.completed.data || []).map(parseRequestData);

        // Update state with all three categories
        set({
          pendingRequests: pendingData,
          approvedWithdraws: approvedData,
          completedRejected: completedData,

          pagination: {
            pending: {
              current: page,
              pageSize: pageSize,
              total: batchData.pending.total || 0
            },
            approvedWithdraws: {
              current: page,
              pageSize: pageSize,
              total: batchData.approved.total || 0
            },
            completedRejected: {
              current: page,
              pageSize: pageSize,
              total: batchData.completed.total || 0
            }
          },

          isLoading: {
            pending: false,
            approvedWithdraws: false,
            completedRejected: false
          },

          lastFetched: {
            pending: currentTime,
            approvedWithdraws: currentTime,
            completedRejected: currentTime
          }
        });

        return {
          pending: batchData.pending,
          approved: batchData.approved,
          completed: batchData.completed,
          metadata: batchData.metadata
        };
      } else {
        throw new Error(`Batch request failed: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('[RequestsStore] Batch request error:', error);

      set({
        isLoading: {
          pending: false,
          approvedWithdraws: false,
          completedRejected: false
        },
        error: {
          pending: error,
          approvedWithdraws: error,
          completedRejected: error
        }
      });

      throw error;
    }
  },

  // Método para actualizar solicitudes individualmente (útil para eventos de socket)
  handleRequestUpdate: async (request: Request) => {

    // Definir categorías de estados
    const terminalStatuses = ["COMPLETED", "REJECTED", "CANCELED"];
    const pendingStatuses = ["PENDING", "REQUIRES_VALIDATION", "PROCESSING"];
    const approvedWithdrawStatuses = ["APPROVED", "WAITING_FOR_CLIENT_VALIDATION"];

    // Determinar a qué lista pertenece la solicitud actualizada
    let targetList: 'pendingRequests' | 'approvedWithdraws' | 'completedRejected' | null = null;

    if (terminalStatuses.includes(request.status)) {
      targetList = 'completedRejected';
    } else if (pendingStatuses.includes(request.status)) {
      targetList = 'pendingRequests';
    } else if (request.type === "WITHDRAW" && approvedWithdrawStatuses.includes(request.status)) {
      targetList = 'approvedWithdraws';
    }

    // Actualizar la lista en memoria de forma optimizada con inmutabilidad garantizada
    let needsRefetch = false;
    let affectedList: 'pending' | 'approvedWithdraws' | 'completedRejected' | null = null;

    set(state => {
      // Buscar la solicitud en todas las listas
      let foundInList: 'pendingRequests' | 'approvedWithdraws' | 'completedRejected' | null = null;
      let foundIndex = -1;

      const listNames = ['pendingRequests', 'approvedWithdraws', 'completedRejected'] as const;
      listNames.forEach(listName => {
        if (Array.isArray(state[listName])) {
          const index = state[listName].findIndex((r: Request) => r.id === request.id);
          if (index !== -1) {
            foundInList = listName;
            foundIndex = index;
          }
        }
      });

      // Si no encontramos la solicitud o no sabemos dónde debe ir, usar comportamiento antiguo
      if (!targetList) {
        console.warn(`[RequestsStore] Estado no manejado para solicitud ${request.type} ${request.id} con estado ${request.status}`);
        return state;
      }

      // CASO 1: La solicitud permanece en la misma lista - UPDATE IN-PLACE (sin parpadeo)
      if (foundInList === targetList && foundIndex !== -1) {

        // Preservar relaciones del existingRequest (user, linkedTransfer, operation)
        const existingRequest = state[targetList][foundIndex];
        const updatedRequest = {
          ...existingRequest,     // Base: request existente con todas sus relaciones
          ...request,             // Override: nuevos datos del socket
          user: request.user || existingRequest.user,  // Preservar user si no viene en socket
          linkedTransfer: request.linkedTransfer || existingRequest.linkedTransfer,
          operation: request.operation || existingRequest.operation,
          uniqueKey: existingRequest.uniqueKey || request.uniqueKey || request.id
        };

        // NO REFETCH - solo update local
        needsRefetch = false;

        // CRITICAL: Create completely new array to guarantee React re-renders
        return {
          ...state,
          [targetList]: [
            ...state[targetList].slice(0, foundIndex),
            updatedRequest,
            ...state[targetList].slice(foundIndex + 1)
          ]
        };
      }
      // CASO 2: La solicitud se mueve a otra lista - MOVE
      else if (foundInList && foundInList !== targetList) {

        // Preservar uniqueKey al mover
        const existingRequest = state[foundInList][foundIndex];
        const updatedRequest = {
          ...request,
          uniqueKey: existingRequest.uniqueKey || request.uniqueKey || request.id
        };

        // Refetch solo la lista de destino
        needsRefetch = true;
        affectedList = targetList === 'pendingRequests' ? 'pending' :
                       targetList === 'approvedWithdraws' ? 'approvedWithdraws' :
                       'completedRejected';

        // CRITICAL: Create completely new state with both lists updated
        return {
          ...state,
          [foundInList]: [
            ...state[foundInList].slice(0, foundIndex),
            ...state[foundInList].slice(foundIndex + 1)
          ],
          [targetList]: [updatedRequest, ...state[targetList]]
        };
      }
      // CASO 3: Nueva solicitud - INSERT
      else {

        // Asegurar uniqueKey para nuevas solicitudes
        const newRequest = {
          ...request,
          uniqueKey: request.uniqueKey || request.id
        };

        // Refetch solo la lista de destino
        needsRefetch = true;
        affectedList = targetList === 'pendingRequests' ? 'pending' :
                       targetList === 'approvedWithdraws' ? 'approvedWithdraws' :
                       'completedRejected';

        // CRITICAL: Create completely new array to guarantee React re-renders
        return {
          ...state,
          [targetList]: [newRequest, ...state[targetList]]
        };
      }
    });

    // Solo refetch si es necesario y solo la lista afectada
    if (needsRefetch && affectedList) {
      if (refreshDebounceTimer) {
        clearTimeout(refreshDebounceTimer);
      }

      refreshDebounceTimer = setTimeout(async () => {
        const state = get();
        const { pagination } = state;


        try {
          await get().fetchRequests(
            affectedList,
            pagination[affectedList].current,
            pagination[affectedList].pageSize,
            {},
            true
          );
        } catch (error) {
          console.error(`[RequestsStore] ❌ Error refetch ${affectedList}:`, error);
        }
      }, REFRESH_DEBOUNCE_MS);
    }
  },
  fetchAndUpdateCounts: async () => {
  const state = get();
  
  try {
    // Cargar datos para cada tipo de solicitud
    await Promise.all([
      state.fetchRequests("pending", 1, 10),
      state.fetchRequests("approvedWithdraws", 1, 10),
      state.fetchRequests("completedRejected", 1, 10),
    ]);
    
    return true;
  } catch (error) {
    console.error("[RequestsStore] Error loading initial data:", error);
    return false;
  }
},
  // Limpiar todos los datos de solicitudes
  clearRequestsData: () => set({
    pendingRequests: [],
    approvedWithdraws: [],
    completedRejected: [],
    lastFetched: {
      pending: null,
      approvedWithdraws: null,
      completedRejected: null,
    },
    pagination: {
      pending: { current: 1, pageSize: 10, total: 0 },
      approvedWithdraws: { current: 1, pageSize: 10, total: 0 },
      completedRejected: { current: 1, pageSize: 10, total: 0 },
    }
  }),
  
  // Actualizar la configuración de paginación
  updatePagination: (
    type: "pending" | "approvedWithdraws" | "completedRejected",
    paginationUpdate: Partial<TablePagination>
  ) => {
    set(state => ({
      pagination: {
        ...state.pagination,
        [type]: {
          ...state.pagination[type],
          ...paginationUpdate
        }
      }
    }));
  }
}));